import AddIcon from "@mui/icons-material/Add";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import {
  Autocomplete,
  Card,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useFormikContext } from "formik";
import React, { useEffect, useState } from "react";

import StyledChip from "@/components/atoms/Chip";
import MDBox from "@/components/atoms/MDBox";
import MDTypography from "@/components/atoms/MDTypography";
import { CampaignFormData } from "@/components/screens/CampaignFormPage/types";
import useToast from "@/contexts/toast-context";
import useGetTags from "@/hooks/requests/tags/use-get-tags";
import { ITag } from "@/interfaces/tag.interface";
import { CampaignFormPageMessages } from "@/messages/campaigns/campaign-form-page.messages";
import { FilterType } from "@/types/filter.types";

type Props = { messages: CampaignFormPageMessages };

function Tags({ messages }: Props) {
  const { values, setFieldValue } = useFormikContext<CampaignFormData>();
  const [tags, setTags] = useState<string[]>([]);
  const [search, setSearch] = useState<string | null>("");
  const [filteredTags, setFilteredTags] = useState<ITag[]>([]);
  const { showToast } = useToast();

  useEffect(() => {
    if (Array.isArray(values.segment.contactTag)) {
      setTags(values.segment.contactTag);
    }
  }, [values.segment.contactTag]);

  const { data, isLoading } = useGetTags({
    pageNumber: 0,
    pageSize: 50,
    filters: [{ property: "name", filterType: FilterType.Contains, value: search || "" }],
    isPaused: !search,
  });

  useEffect(() => {
    if (data && data.items) setFilteredTags(data.items);
  }, [data]);

  const handleAddTag = (newTag: string) => {
    const trimmedTag = newTag.trim();

    if (trimmedTag !== "" && !tags.includes(trimmedTag)) {
      const updatedTags = [...tags, trimmedTag];
      setTags(updatedTags);
      setFieldValue("segment.contactTag", updatedTags);

      if (
        !filteredTags.find((tag) => tag.name.toLocaleUpperCase() === trimmedTag.toLocaleUpperCase())
      ) {
        setFieldValue("newContactTags", [...values.newContactTags, trimmedTag]);
      }

      setSearch("");
    } else if (tags.includes(trimmedTag)) {
      showToast({ text: messages.tagExistsError, type: "error" });
    }
  };

  const handleDeleteTag = (tagToDelete: string) => {
    const updatedTags = tags.filter((tag) => tag !== tagToDelete);
    setTags(updatedTags);
    setFieldValue("segment.contactTag", updatedTags);

    setFieldValue(
      "newContactTags",
      values.newContactTags.filter((tag) => tag !== tagToDelete)
    );
  };

  const handleInputKeyDown = () => {
    if (!search || values.disabledField) return;

    const trimmedSearch = search.trim();

    if (trimmedSearch !== "" && !tags.includes(trimmedSearch)) {
      handleAddTag(trimmedSearch);
    } else if (tags.includes(trimmedSearch)) {
      showToast({ text: messages.tagExistsError, type: "error" });
    }
  };
  const disabledInput = typeof search !== "string" || search.trim() === "" || values.disabledField;

  return (
    <Card sx={{ height: "100%" }}>
      <MDBox p={3}>
        <MDBox display="flex" justifyContent="space-between">
          <MDTypography variant="h5" fontWeight="bold">
            {messages.contactTagTitle}
          </MDTypography>
        </MDBox>

        <MDBox mt={1}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 12 }}>
              <Autocomplete
                sx={{
                  "& .MuiAutocomplete-inputRoot": {
                    paddingRight: "0px!important",
                  },
                }}
                freeSolo
                value={search || ""}
                onInputChange={(_event, newValue) => setSearch(newValue)}
                selectOnFocus
                clearOnBlur
                handleHomeEndKeys
                options={filteredTags}
                disabled={values.disabledField}
                getOptionLabel={(option) => (typeof option === "string" ? option : option.name)}
                renderOption={(props, option) => (
                  <li
                    {...props}
                    key={typeof option === "string" ? option : option.id || option.name}
                  >
                    {typeof option === "string" ? option : option.name}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="standard"
                    label={messages.addTagLabel}
                    fullWidth
                    onKeyPress={(e) => {
                      if (e.key === "Enter" && !e.shiftKey && search && search.trim()) {
                        e.preventDefault();
                        handleInputKeyDown();
                      }
                    }}
                    slotProps={{
                      input: {
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                            <InputAdornment position="end" sx={{ marginRight: "-10px" }}>
                              <IconButton
                                onClick={() => handleAddTag(search || "")}
                                disabled={disabledInput}
                              >
                                <AddIcon />
                              </IconButton>
                            </InputAdornment>
                          </>
                        ),
                      },

                      inputLabel: { shrink: true },
                    }}
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 12 }} sx={{ overflowY: "scroll", height: 100 }}>
              <MDBox display="flex" flexWrap="wrap" gap={1}>
                {tags.map((tag, index) => (
                  <StyledChip
                    key={`${index}-${tag}`}
                    variant="outlined"
                    label={tag}
                    deleteIcon={<HighlightOffIcon fontSize="medium" style={{ color: "white" }} />}
                    onDelete={() => handleDeleteTag(tag)}
                    disabled={values.disabledField}
                  />
                ))}
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>
      </MDBox>
    </Card>
  );
}

export default Tags;
